import React, { createContext, useState, useEffect, useContext, useCallback, type ReactNode } from 'react';
import { authService } from './authService';
import type { ApiRecord, AuthSession } from './types';

interface AuthContextValue {
  user: ApiRecord | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<AuthSession>;
  logout: () => Promise<void>;
  checkAndRefreshSession: () => Promise<boolean>;
  refreshUser: () => Promise<ApiRecord | null>;
  updateUserData: (updates: ApiRecord) => void;
  clearError: () => void;
}

// Create the authentication context
const AuthContext = createContext<AuthContextValue | null>(null);

// AuthProvider component that wraps the application
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<ApiRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  // Initialize the auth context on component mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        setLoading(true);

        if (authService.isAuthenticated()) {
                    try {
            const userData = await authService.getCurrentUser();
                        setUser(userData);
          } catch (userError) {
            console.error('Failed to get user data despite authentication:', userError);
            // If we can't get user data, consider the user not authenticated
            authService.clearSession();
          }
        } else {
                  }
      } catch (err) {
        console.error('Auth initialization error:', err);
        setError(err.message);
        // Clear invalid auth data
        authService.clearSession();
      } finally {
        setLoading(false);
        setAuthChecked(true);
      }
    };

    initAuth();

    // Setup an interval to periodically check authentication status
    const authCheckInterval = setInterval(() => {
      // Only perform checks if we've completed the initial check
      if (authChecked) {
        if (!authService.isAuthenticated() && user) {
                    setUser(null);
        }
      }
    }, 60000); // Check every minute

    // Clean up the interval on unmount
    return () => clearInterval(authCheckInterval);
  }, [authChecked]);

  // Login function
  const login = async (username: string, password: string): Promise<AuthSession> => {
    try {
      setLoading(true);
      setError(null);

      const authData = await authService.login(username, password);

      if (authData.user) {
                setUser(authData.user);
      } else {
                const userData = await authService.getCurrentUser();
        setUser(userData);
      }

      return authData;
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = useCallback(async () => {
    try {
      setLoading(true);
            await authService.logout();
      setUser(null);
          } catch (err) {
      console.error('Logout error:', err);
      setError(err.message);
      // Even if the server logout fails, clear local state
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Check session validity and refresh if needed
  const checkAndRefreshSession = useCallback(async () => {
    try {
      if (!authService.isAuthenticated()) {
                setUser(null);
        return false;
      }

      // Proactively refresh the token if it expires within the next 5 minutes.
      // The token itself is in an HttpOnly cookie — we only check the locally
      // stored expiry timestamp as a hint.
      const expires = authService.getTokenExpiry();

      if (expires) {
        const now = new Date().getTime() / 1000;
        if (now >= expires - 300) {
                    try {
            await authService.refreshToken();
                      } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            setUser(null);
            return false;
          }
        }
      }

      return true;
    } catch (err) {
      console.error('Session check error:', err);
      setUser(null);
      return false;
    }
  }, []);

  // Refresh user data from server
  const refreshUser = useCallback(async () => {
    try {
      if (authService.isAuthenticated()) {
                const userData = await authService.getCurrentUser(true); // Force fetch from server
        setUser(userData);
                return userData;
      }
      return null;
    } catch (err) {
      console.error('Failed to refresh user data:', err);
      throw err;
    }
  }, []);

  // Update user data locally (without fetching from server)
  const updateUserData = useCallback((updates: ApiRecord) => {
    setUser(prevUser => ({
      ...prevUser,
      ...updates
    }));
  }, []);

  // Value object to be provided to consumers
  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    logout,
    checkAndRefreshSession,
    refreshUser,
    updateUserData,
    clearError: () => setError(null)
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
